import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Menu,
  MenuItem,
  Stack,
  Alert,
  Backdrop,
  CircularProgress,
  Divider,
  useTheme,
  useMediaQuery,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FileUpload as FileUploadIcon,
  FileDownload as FileDownloadIcon,
  Refresh as RefreshIcon,
  Apartment as ProjectIcon,
} from "@mui/icons-material";
import {
  readExcelFile,
  exportAsExcel,
  exportAsCSV,
  exportAsPDF,
  downloadExcelFile,
} from "../../../utils/importExport";
import { hasReadPermission, hasWritePermission } from '../../../utils/menuFilter';
import WarningIcon from '@mui/icons-material/Warning';
import AvatarMui from '@mui/material/Avatar';
import { showSnackbar } from "../../../components/Snackbar";
import { getProjects, getPlotsByProject, searchFloorData, updateFloor, deleteFloor, createFloor, createFlat } from "../../../services/project";

const FloorList = () => {
  const history = useHistory();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const canRead = hasReadPermission('project.apartment-commercial') || hasReadPermission('project');
  const canWrite = hasWritePermission('project.apartment-commercial') || hasWritePermission('project');

  const [draftType, setDraftType] = useState("all");
  const [draftProject, setDraftProject] = useState("all");
  const [draftBuilding, setDraftBuilding] = useState("all");
  const [draftFloor, setDraftFloor] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [hasSearched, setHasSearched] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [activeSelection, setActiveSelection] = useState({
    type: "",
    project: "",
    building: "",
    floor: "",
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [floorStore, setFloorStore] = useState({});
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [dialogError, setDialogError] = useState("");
  const [newFloor, setNewFloor] = useState({
    floorNo: "",
    name: "",
    totalFlats: "",
    status: "",
    remark: "",
  });
  const [editFloorState, setEditFloorState] = useState({
    id: "",
    buildingId: "",
    originalBuildingId: "",
    floorNo: "",
    name: "",
    totalFlats: "",
    status: "",
    remark: "",
  });
  const [addDialogType, setAddDialogType] = useState("");
  const [addDialogProject, setAddDialogProject] = useState("");
  const [addDialogBuildingId, setAddDialogBuildingId] = useState("");
  const [editDialogType, setEditDialogType] = useState("");
  const [editDialogProject, setEditDialogProject] = useState("");
  const fileInputRef = useRef(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFileName, setImportFileName] = useState("");
  const [selectedImportFile, setSelectedImportFile] = useState(null);
  const [importError, setImportError] = useState("");
  const [importSuccess, setImportSuccess] = useState("");
  const [exportMenuAnchorEl, setExportMenuAnchorEl] = useState(null);
  const [hasAutoSearched, setHasAutoSearched] = useState(false);

  const [projects, setProjects] = useState([]);
  const [apiBuildings, setApiBuildings] = useState([]);
  const [addDialogBuildings, setAddDialogBuildings] = useState([]);
  const [globalLoading, setGlobalLoading] = useState(false);
  const lastProjectTypeLoaded = useRef(null);

  const extractInventories = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.inventories)) return payload.inventories;
    if (Array.isArray(payload?.data?.inventories)) return payload.data.inventories;
    if (Array.isArray(payload?.data)) return payload.data;
    return [];
  };

  const loadProjectsByType = useCallback(
    async (type) => {
      if (!type || type === "all") {
        lastProjectTypeLoaded.current = null;
        setProjects([]);
        return;
      }
      if (lastProjectTypeLoaded.current === type) return;
      lastProjectTypeLoaded.current = type;
      try {
        const data = await getProjects({ projectType: type });
        const list = Array.isArray(data) ? data : [];
        setProjects(list);
        if (list.length === 1 && (draftProject === "all" || !draftProject)) {
          setDraftProject(list[0]._id || list[0].id);
        }
      } catch (e) {
        setProjects([]);
      }
    },
    []
  );

  useEffect(() => {
    loadProjectsByType(draftType);
  }, [draftType, loadProjectsByType]);

  useEffect(() => {
    if (!addDialogType) return;
    loadProjectsByType(addDialogType);
  }, [addDialogType, loadProjectsByType]);

  useEffect(() => {
    if (!editDialogType) return;
    loadProjectsByType(editDialogType);
  }, [editDialogType, loadProjectsByType]);

  const selectedProjectId = useMemo(() => {
    if (!draftProject || draftProject === 'all') return '';
    return String(draftProject);
  }, [draftProject]);
  const canAddFloor = Boolean(selectedProjectId);

  // Load buildings after Property Type and Project are selected
  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        if (!draftType || draftType === 'all' || !selectedProjectId) {
          if (active) setApiBuildings([]);
          return;
        }

        const inventoryType = draftType;
        const projectType = draftType;

        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.debug('[FloorList] fetching buildings', {
            projectId: selectedProjectId,
            inventoryType,
            projectType,
          });
        }

        const response = await getPlotsByProject(
          selectedProjectId,
          inventoryType,
          projectType,
        );
        const inventories = extractInventories(response);
        const buildings = inventories
          .filter((building) => {
            const bType = (building.inventoryType || "Apartment").toLowerCase();
            const targetType = inventoryType.toLowerCase();
            return bType === targetType || (targetType === 'commercial' && bType === 'apartment') || (targetType === 'apartment' && bType === 'commercial');
          })
          .map((b) => ({
            id: b._id || b.id || '',
            name: b.buildingName || b.name || 'Unnamed Building',
            projectId: b.projectId || b.projectData?._id || b.project_id || '',
            projectName: b.projectName || b.projectData?.projectName || b.project || '',
            type: b.projectType || b.projectData?.projectType || b.inventoryType || 'Apartment',
          }));
        if (active) {
          setApiBuildings(buildings);
          if (buildings.length === 1 && (draftBuilding === "all" || !draftBuilding)) {
            setDraftBuilding(buildings[0].id);
          }
        }
      } catch (e) {
        if (active) setApiBuildings([]);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [draftType, selectedProjectId]);

  const runWithLoader = useCallback(
    async (task) => {
      setGlobalLoading(true);
      try {
        await task();
      } finally {
        setGlobalLoading(false);
      }
    },
    []
  );

  const resetImportState = () => {
    setImportFileName("");
    setSelectedImportFile(null);
    setImportError("");
    setImportSuccess("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleImportClick = () => {
    resetImportState();
    setImportDialogOpen(true);
  };

  const handleCloseImportDialog = () => {
    resetImportState();
    setImportDialogOpen(false);
  };

  const handleChooseImportFile = (event) => {
    const file = event.target.files && event.target.files[0];
    if (file) {
      setSelectedImportFile(file);
      setImportFileName(file.name);
    }
  };

  const handleOpenExportMenu = (event) => {
    setExportMenuAnchorEl(event.currentTarget);
  };

  const handleCloseExportMenu = () => {
    setExportMenuAnchorEl(null);
  };

  const parseTotalFlatsValue = (value) => {
    if (value === null || value === undefined || value === "") return "";
    const num = Number(value);
    return Number.isFinite(num) ? num : value;
  };

  const handleFloorNoChange = (setter) => (e) => {
    const val = e.target.value;
    setter((prev) => ({
      ...prev,
      floorNo: val,
      name: val ? (parseInt(val, 10) === 0 ? "Ground Floor" : `Floor ${val}`) : ""
    }));
  };


  const handlePerformImport = async () => {
    if (!selectedImportFile) return;
    try {
      const rows = await readExcelFile(selectedImportFile);
      if (!Array.isArray(rows) || rows.length === 0) {
        throw new Error("Excel sheet is empty or invalid.");
      }

      const timestamp = Date.now();
      const fallbackBuildingId =
        activeSelection.building || (draftBuilding !== "all" ? draftBuilding : "");

      const groupedFloors = rows.reduce((acc, row, index) => {
        const buildingId =
          row.buildingId ||
          row.BuildingId ||
          row["Building Id"] ||
          row["BuildingID"] ||
          fallbackBuildingId;

        const name = row.name || row.Name || row.floorName || row.FloorName || "";
        if (!buildingId || !name) {
          return acc;
        }

        const totalFlatsRaw =
          row.totalFlats || row.TotalFlats || row["Total Flats"] || row.Total || "";
        const floor = {
          id: row.id || row.ID || row.floorId || row.FloorId || `IMP-FLR-${timestamp}-${index + 1}`,
          name,
          totalFlats: parseTotalFlatsValue(totalFlatsRaw),
          status: row.status || row.Status || "Available",
          remark: row.remark || row.Remark || "",
        };

        if (!acc[buildingId]) {
          acc[buildingId] = [];
        }
        acc[buildingId].push(floor);
        return acc;
      }, {});

      const updatedBuildingIds = Object.keys(groupedFloors);
      if (!updatedBuildingIds.length) {
        throw new Error("No valid floor records were found in the file.");
      }

      setFloorStore((prev) => {
        const next = { ...prev };
        updatedBuildingIds.forEach((buildingId) => {
          next[buildingId] = groupedFloors[buildingId];
        });
        return next;
      });

      setImportSuccess(`Imported floors for ${updatedBuildingIds.length} building(s) successfully.`);
      setImportError("");
      setHasSearched(false);
      setActiveSelection({ type: "", project: "", building: "", floor: "" });
      setDraftType("all");
      setDraftProject("all");
      setDraftBuilding("all");
      setDraftFloor("all");
    } catch (error) {
      setImportError(error.message || "Failed to import floors. Please check the file format.");
      setImportSuccess("");
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleDownloadSample = () => {
    try {
      downloadExcelFile({
        rows: [
          {
            buildingId: "BLD-001",
            buildingName: "Emerald Heights - Tower A",
            project: "Emerald Heights",
            floorId: "FLR-201",
            name: "Sample Floor",
            totalFlats: 10,
            status: "Available",
            remark: "Sample data row",
          },
        ],
        sheetName: "Sample",
        fileName: "floor_import_sample.xlsx",
      });
    } catch (error) {
      setImportError(error.message || "Failed to download sample file.");
    }
  };

  const handleDownloadTemplate = () => {
    try {
      downloadExcelFile({
        rows: [
          {
            buildingId: "",
            buildingName: "",
            project: "",
            floorId: "",
            name: "",
            totalFlats: "",
            status: "",
            remark: "",
          },
        ],
        sheetName: "Template",
        fileName: "floor_import_template.xlsx",
      });
    } catch (error) {
      setImportError(error.message || "Failed to download template file.");
    }
  };


  useEffect(() => {
    const incomingBuilding = location.state?.building;
    const incomingFloor = location.state?.floor;
    if (incomingBuilding) {
      const normalizedType = incomingBuilding.type || incomingBuilding.projectType || "Apartment";
      const project = incomingBuilding.project || incomingBuilding.projectId || "all";
      const buildingId = incomingBuilding.id || "all";
      setDraftType(normalizedType);
      setDraftProject(project);
      setDraftBuilding(buildingId);
      if (incomingFloor?.id) {
        setDraftFloor(incomingFloor.id);
      }
      setActiveSelection({
        type: normalizedType,
        project,
        building: buildingId,
        floor: incomingFloor?.id || "",
      });
      setHasSearched(true);
      setHasAutoSearched(false); // Reset auto-search flag for new navigation
      setPage(0);

      // Auto-search after a brief delay to ensure all state is updated
      setTimeout(() => {
        if (buildingId !== "all") {
          handleSearch();
          setHasAutoSearched(true);
        }
      }, 100);
    } else {
      // Reset auto-search flag when not redirected
      setHasAutoSearched(false);
    }
  }, [location.state]);

  useEffect(() => {
    if (!projects.length) return;
    if (!draftProject || draftProject === 'all') return;
    const existsById = projects.some((p) => String(p._id || p.id) === String(draftProject));
    if (existsById) return;
    const match = projects.find((p) => (p.projectName || p.name) === draftProject);
    if (match) {
      setDraftProject(match._id || match.id);
    }
  }, [projects]);

  const projectOptions = useMemo(() => {
    const opts = (projects || []).map((p) => ({
      value: p._id || p.id,
      label: p.projectName || p.name || 'Unnamed',
    }));
    return [{ value: 'all', label: 'Select Project' }, ...opts];
  }, [projects]);

  const selectedFloors = useMemo(() => {
    if (!hasSearched) return [];
    if (!activeSelection.building) return [];
    return floorStore[activeSelection.building] || [];
  }, [floorStore, activeSelection.building, hasSearched]);

  const statusOptions = useMemo(() => {
    return ["all", "Available", "Under Construction"];
  }, []);

  const filteredSelectedFloors = useMemo(() => {
    let list = selectedFloors;
    if (activeSelection.floor) {
      list = list.filter((floor) => String(floor.id) === String(activeSelection.floor));
    }
    if (statusFilter && statusFilter !== "all") {
      list = list.filter(
        (floor) => (floor.status || "").toLowerCase() === statusFilter.toLowerCase(),
      );
    }
    return list;
  }, [selectedFloors, statusFilter, activeSelection.floor]);

  const paginatedFloors = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredSelectedFloors.slice(start, start + rowsPerPage);
  }, [filteredSelectedFloors, page, rowsPerPage]);

  // Property Type is the only static option; everything else comes from API
  const propertyTypeOptions = useMemo(() => ["Apartment", "Commercial"], []);

  const exportRows = useMemo(() => {
    const formatRow = (buildingId, floor) => {
      const building =
        apiBuildings.find(
          (item) => item.id === buildingId || item._id === buildingId,
        ) || null;
      return {
        Building: building?.name || "",
        Floor: floor.name,
        "Total Flats": floor.totalFlats,
        Status: floor.status,
        Remark: floor.remark,
      };
    };

    const entries = [];
    if (hasSearched && activeSelection.building && selectedFloors.length) {
      entries.push([activeSelection.building, selectedFloors]);
    } else {
      entries.push(...Object.entries(floorStore));
    }
    return entries.flatMap(([buildingId, floors]) =>
      (floors || []).map((floor) => formatRow(buildingId, floor))
    );
  }, [floorStore, apiBuildings, hasSearched, activeSelection.building, selectedFloors]);

  const handleExportExcel = async () => {
    try {
      const projectIdArg = draftProject !== "all" ? draftProject : undefined;
      const buildingIdArg = draftBuilding !== "all" ? draftBuilding : undefined;

      if (!buildingIdArg) {
        handleCloseExportMenu();
        return;
      }

      const base = process.env.REACT_APP_API_BASE_URL;
      const params = new URLSearchParams({
        buildingId: buildingIdArg,
        page: "1",
        limit: "100000",
      });
      if (projectIdArg) params.set("projectId", projectIdArg);

      const token = typeof localStorage !== "undefined" ? localStorage.getItem("token") : null;
      const response = await fetch(`${base}/api/floor?${params.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const body = await response.json().catch(() => ({}));
      const floors = Array.isArray(body)
        ? body
        : Array.isArray(body?.data)
          ? body.data
          : Array.isArray(body?.floors)
            ? body.floors
            : [];

      const rows = floors.map((f, idx) => {
        const totalFlatsValue = Number.isFinite(Number(f.totalFlats || f.totalFlat))
          ? Number(f.totalFlats || f.totalFlat)
          : "";
        const floorLabel =
          f.name ||
          f.floorName ||
          (Number.isFinite(Number(f.floorNo)) ? `Floor ${f.floorNo}` : `Floor ${idx + 1}`);
        return {
          Floor: floorLabel,
          Project: f.projectName || "",
          "Total Flats": totalFlatsValue,
          Status: f.status || "available",
          Remark: f.remark || "",
        };
      });

      if (!rows.length || !rows.some((r) => r.Floor)) {
        handleCloseExportMenu();
        return;
      }

      exportAsExcel({ rows, sheetName: "Floors", fileNameBase: "floors" });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Export floors error", error);
    } finally {
      handleCloseExportMenu();
    }
  };

  const handleExportCSV = () => {
    if (!exportRows.length) {
      handleCloseExportMenu();
      return;
    }
    exportAsCSV({ rows: exportRows, fileNameBase: "floors" });
    handleCloseExportMenu();
  };

  const handleExportPDF = () => {
    if (!exportRows.length) {
      handleCloseExportMenu();
      return;
    }
    const headers = Object.keys(exportRows[0] || {});
    exportAsPDF({
      rows: exportRows,
      headers,
      title: "Floor Management",
      fileNameBase: "floors",
      headStyles: { fillColor: [25, 118, 210] },
    });
    handleCloseExportMenu();
  };

  const projectsForAddDialog = useMemo(() => {
    if (!addDialogType) return [];
    return (projects || [])
      .filter(
        (p) =>
          (p.type || "").toLowerCase() === addDialogType.toLowerCase(),
      )
      .map((p) => ({
        id: p._id || p.id,
        name: p.projectName || p.name || "Unnamed",
      }));
  }, [addDialogType, projects]);

  const buildingsForAddDialog = useMemo(() => addDialogBuildings, [addDialogBuildings]);

  const projectsForEditDialog = useMemo(() => {
    if (!editDialogType) return [];
    return (projects || [])
      .filter(
        (p) =>
          (p.type || "").toLowerCase() === editDialogType.toLowerCase(),
      )
      .map((p) => ({
        id: p._id || p.id,
        name: p.projectName || p.name || "Unnamed",
      }));
  }, [editDialogType, projects]);

  const buildingsForEditDialog = useMemo(() => {
    if (!editDialogType || !editDialogProject) return [];
    return apiBuildings.filter((building) => {
      const buildingProjectId = building.projectId || "";
      return String(buildingProjectId) === String(editDialogProject);
    });
  }, [editDialogType, editDialogProject, apiBuildings]);

  // Load buildings for Add Floor modal based on selected type + project
  useEffect(() => {
    let active = true;
    const loadAddBuildings = async () => {
      try {
        if (!addDialogType || !addDialogProject) {
          if (active) setAddDialogBuildings([]);
          return;
        }
        const inventoryType = addDialogType;
        const projectType = addDialogType;
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.debug("[FloorList] (Add Dialog) fetching buildings", {
            projectId: addDialogProject,
            inventoryType,
            projectType,
          });
        }
        const response = await getPlotsByProject(
          addDialogProject,
          inventoryType,
          projectType,
        );
        const inventories = extractInventories(response);
        const buildings = inventories.map((b) => ({
          id: b._id || b.id || "",
          name: b.buildingName || b.name || "Unnamed Building",
          projectId: b.projectId || b.projectData?._id || b.project_id || "",
          projectName: b.projectName || b.projectData?.projectName || b.project || "",
          type: b.projectType || b.projectData?.projectType || b.inventoryType || "",
        }));
        if (active) setAddDialogBuildings(buildings);
      } catch (e) {
        if (active) setAddDialogBuildings([]);
      }
    };
    loadAddBuildings();
    return () => {
      active = false;
    };
  }, [addDialogType, addDialogProject]);

  const statusChipStyle = (status) => {
    switch (status) {
      case "Available":
        return { backgroundColor: "rgba(76, 175, 80, 0.12)", color: "#2e7d32" };
      case "Under Construction":
        return { backgroundColor: "rgba(255, 193, 7, 0.12)", color: "#f57c00" };
      default:
        return { backgroundColor: "rgba(158, 158, 158, 0.12)", color: "#616161" };
    }
  };

  const handleSearch = async () => {
    try {
      setSearchError("");
      const projectIdArg = draftProject !== 'all' ? draftProject : undefined;
      const buildingIdArg = draftBuilding !== 'all' ? draftBuilding : undefined;

      await runWithLoader(async () => {
        const floors = await searchFloorData({ projectId: projectIdArg, buildingId: buildingIdArg });
        const normalized = (floors || []).map((f, idx) => {
          const building = apiBuildings.find(b => b.id === f.buildingId || b._id === f.buildingId);
          return {
            id: f._id || f.id || f.floorId || `FLR-${idx + 1}`,
            name: f.floorNo,
            totalFlats: Number.isFinite(Number(f.totalFlats || f.totalFlat)) ? Number(f.totalFlats || f.totalFlat) : (f.totalFlats || f.totalFlat) || 0,
            projectName: f.projectName || f.project || '',
            buildingName: building?.name || f.buildingName || '',
            status: f.status || 'Available',
            remark: f.remark || f.notes || '',
          };
        });

        const bucketKey = buildingIdArg || 'all';
        setFloorStore((prev) => ({
          ...prev,
          [bucketKey]: normalized,
        }));
      });
      setHasSearched(true);
      setActiveSelection({
        type: draftType !== 'all' ? draftType : '',
        project: projectIdArg || '',
        building: draftBuilding !== 'all' ? draftBuilding : (buildingIdArg || 'all'),
        floor: draftFloor !== "all" ? draftFloor : "",
      });
      setPage(0);
    } catch (e) {
      setSearchError("Failed to fetch floors. Please try again.");
      setHasSearched(false);
    }
  };

  const handleReset = () => {
    setDraftType("all");
    setDraftProject("all");
    setDraftBuilding("all");
    setDraftFloor("all");
    setStatusFilter("all");
    setActiveSelection({ type: "", project: "", building: "", floor: "" });
    setHasSearched(false);
    setSearchError("");
    setPage(0);
  };

  const getSelectedBuilding = () =>
    apiBuildings.find(
      (building) =>
        building.id === activeSelection.building ||
        building._id === activeSelection.building,
    ) || null;

  const handleViewFlats = (floor) => {
    const building = getSelectedBuilding();
    history.push("/app/project/apartment/flats", { building, floor });
  };

  const handleOpenAddDialog = () => {
    setNewFloor({
      name: "",
      totalFlats: "",
      status: "",
      remark: "",
    });
    const defaultBuildingId = activeSelection.building || (draftBuilding !== "all" ? draftBuilding : "");
    const defaultBuilding = defaultBuildingId
      ? apiBuildings.find(
        (building) =>
          building.id === defaultBuildingId ||
          building._id === defaultBuildingId,
      ) || null
      : null;
    const inferredType = defaultBuilding
      ? defaultBuilding.type || ""
      : draftType !== "all"
        ? draftType
        : "";
    const inferredProjectId = defaultBuilding
      ? defaultBuilding.projectId || ""
      : draftProject !== "all"
        ? draftProject
        : "";
    setAddDialogType(inferredType);
    setAddDialogProject(inferredProjectId);
    setAddDialogBuildingId(defaultBuilding ? defaultBuilding.id : "");
    setDialogError("");
    setAddDialogOpen(true);
  };

  const handleSaveNewFloor = async () => {
    if (!addDialogType || !addDialogProject || !addDialogBuildingId) {
      setDialogError("Please select property type, project, and building.");
      return;
    }
    const buildingId = addDialogBuildingId;
    const { name, totalFlats, status, remark } = newFloor;
    if (!name || !totalFlats || !status) {
      setDialogError("Please fill in all required fields.");
      return;
    }
    const totalFlatsNum = Number(totalFlats);
    if (Number.isNaN(totalFlatsNum)) {
      setDialogError("Total flats must be numeric.");
      return;
    }

    try {
      setDialogError("");

      const building = apiBuildings.find((b) => b.id === buildingId || b._id === buildingId);
      const buildingName = building ? building.name : "";
      const project = projects.find((p) => p._id === addDialogProject || p.id === addDialogProject);
      const projectName = project ? project.projectName || project.name : addDialogProject;

      const floorNumberMatch = name.match(/\d+/);
      const floorNo = floorNumberMatch
        ? parseInt(floorNumberMatch[0], 10)
        : name.toLowerCase().includes("ground")
          ? 0
          : 1;

      const statusMap = {
        "Available": "available",
        "Under Construction": "underConstruction",
      };
      const apiStatus = statusMap[status] || status.toLowerCase().replace(" ", "_");

      await runWithLoader(async () => {
        const floorRes = await createFloor({
          projectId: addDialogProject,
          buildingId,
          floorNo,
          totalFlat: totalFlatsNum,
          buildingName,
          projectName,
          status: apiStatus,
          remark,
          updatedBy: "Admin",
        });

        // Generate flats if floor creation was successful and flat count > 0
        const floorId = floorRes?.data?._id || floorRes?.data?.id || floorRes?._id || floorRes?.id;
        if (floorId && totalFlatsNum > 0) {
          const flatPromises = [];
          for (let i = 1; i <= totalFlatsNum; i++) {
            const flatNo = Number(floorNo) * 100 + i;
            flatPromises.push(
              createFlat({
                projectId: addDialogProject,
                buildingId,
                floorId,
                flatNumber: flatNo,
                flatType: "2 BHK", // Default placeholder
                facing: "East",   // Default placeholder
                status: "available",
                inventoryType: activeSelection.type || draftType || "Apartment",
                remark: "",
                price: 0,
                plcPercent: 0,
                companyDiscountPercent: 0,
                cost: 0,
                guidelineValue: 0
              })
            );
          }
          try {
            await Promise.all(flatPromises);
          } catch (err) {
            console.error("[FloorList] Partial flat generation failure", err);
          }
        }

        // Refresh floors list from API to stay in sync
        await handleSearch();
      });

      setAddDialogOpen(false);
      setHasSearched(true);
      setNewFloor({
        name: "",
        totalFlats: "",
        status: "",
        remark: "",
      });
      setPage(0);
      try {
        showSnackbar({ type: "success", message: "successsfully added the flatlist" });
      } catch {
        // ignore snackbar errors
      }
    } catch (error) {
      console.error("handleSaveNewFloor: Error while creating floor", error);
      setDialogError(error.message || "Failed to add floor. Please try again.");
    }
  };

  const handleEditFloor = (floor) => {
    const fallbackBuildingId = activeSelection.building || (draftBuilding !== "all" ? draftBuilding : "");
    const buildingId = fallbackBuildingId || Object.entries(floorStore).find(([id, floors]) => floors.some((item) => item.id === floor.id))?.[0] || "";

    const buildingDetails =
      apiBuildings.find(
        (building) => building.id === buildingId || building._id === buildingId,
      ) || null;
    const selectedType =
      activeSelection.type ||
      draftType ||
      (buildingDetails ? buildingDetails.type : "");
    const selectedProject =
      activeSelection.project ||
      draftProject ||
      (buildingDetails ? buildingDetails.projectId || "" : "");


    setEditDialogType(selectedType);
    setEditDialogProject(selectedProject);
    const floorNumberMatch = (floor.name || "").match(/\d+/);
    const floorNo = floorNumberMatch ? floorNumberMatch[0] : (floor.name?.toLowerCase().includes("ground") ? "0" : "");

    setEditFloorState({
      id: floor.id,
      buildingId,
      originalBuildingId: buildingId,
      floorNo: floorNo,
      name: floor.name,
      totalFlats: floor.totalFlats,
      status: floor.status,
      remark: floor.remark || "",
    });
    setDialogError("");
    setEditDialogOpen(true);
  };

  const handleUpdateFloor = async () => {
    const { id, buildingId, originalBuildingId, name, totalFlats, status, remark } = editFloorState;

    if (!editDialogType || !editDialogProject || !buildingId) {
      setDialogError("Please select property type, project, and building.");
      return;
    }

    if (!totalFlats || !status) {
      setDialogError("Please fill in all required fields.");
      return;
    }

    const totalFlatsNum = Number(totalFlats);
    if (Number.isNaN(totalFlatsNum)) {
      setDialogError("Total flats must be numeric.");
      return;
    }

    setDialogError("");

    try {
      setEditSaving(true);

      const building = apiBuildings.find((b) => b.id === buildingId);
      const buildingName = building ? building.name : "";
      const project = projects.find((p) => p._id === editDialogProject);
      const projectName = project ? project.projectName : editDialogProject;

      const floorNumberMatch = name.match(/\d+/);
      const floorNo = floorNumberMatch ? parseInt(floorNumberMatch[0], 10) : (name.toLowerCase().includes('ground') ? 0 : 1);

      const statusMap = {
        'Available': 'available',
        'Under Construction': 'underConstruction',
      };
      const apiStatus = statusMap[status] || status.toLowerCase().replace(' ', '_');

      await runWithLoader(async () => {
        await updateFloor(id, {
          floorNo,
          totalFlat: totalFlatsNum,
          buildingName,
          projectName,
          status: apiStatus,
          remark,
          updatedBy: "Admin"
        });
      });

      const targetBuildingId = buildingId;
      const sourceBuildingId = originalBuildingId || buildingId;

      setFloorStore((prev) => {
        const next = { ...prev };
        const updatedFloor = { id, name, totalFlats: totalFlatsNum, status, remark };

        if (sourceBuildingId !== targetBuildingId) {
          next[sourceBuildingId] = (next[sourceBuildingId] || []).filter((item) => item.id !== id);
          const targetList = next[targetBuildingId] ? [...next[targetBuildingId]] : [];
          const existingIndex = targetList.findIndex((item) => item.id === id);
          if (existingIndex >= 0) {
            targetList[existingIndex] = updatedFloor;
          } else {
            targetList.push(updatedFloor);
          }
          next[targetBuildingId] = targetList;
        } else {
          next[targetBuildingId] = (next[targetBuildingId] || []).map((item) =>
            item.id === id ? updatedFloor : item
          );
        }
        return next;
      });

      setEditFloorState((prev) => ({ ...prev, originalBuildingId: targetBuildingId }));

      setEditDialogOpen(false);

      setHasSearched(true);

      setActiveSelection({
        type: editDialogType,
        project: editDialogProject,
        building: targetBuildingId,
        floor: id,
      });


      setDraftType(editDialogType);

      setDraftProject(editDialogProject);

      setDraftBuilding(targetBuildingId);

      setDraftFloor(id);


    } catch (error) {
      console.error("handleUpdateFloor: Error occurred during floor update", error);
      setDialogError(error.message || "Failed to update floor. Please try again.");
    } finally {
      setEditSaving(false);
    }
  };

  const handleDeleteFloor = async (floor) => {
    if (!window.confirm(`Delete ${floor.name}? This action cannot be undone.`)) {
      return;
    }

    try {
      await runWithLoader(async () => {
        await deleteFloor(floor.id);

        const buildingId = activeSelection.building;
        setFloorStore((prev) => {
          const next = { ...prev };
          next[buildingId] = (next[buildingId] || []).filter((item) => item.id !== floor.id);
          return next;
        });
      });
      setDraftFloor("all");
      setActiveSelection((prev) => ({ ...prev, floor: "" }));
    } catch (error) {
      console.error("handleDeleteFloor: Error deleting floor", error);
      alert(`Failed to delete floor: ${error.message || "Unknown error"}`);
    }
  };

  const selectedBuilding = getSelectedBuilding();
  const selectedFloorItem =
    activeSelection.floor && selectedFloors.length
      ? selectedFloors.find((floor) => floor.id === activeSelection.floor) || null
      : null;

  const handleTotalFlatsChange = (setter) => (e) => {
    const raw = e.target.value;
    if (raw === "") {
      setter((prev) => ({ ...prev, totalFlats: "" }));
      return;
    }
    if (String(raw).length > 3) return;
    const num = Number(raw);
    if (Number.isNaN(num) || num < 0) return;
    setter((prev) => ({ ...prev, totalFlats: raw }));
  };

  if (!canRead) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '70vh', p: 3 }}>
        <Paper elevation={3} sx={{ p: { xs: 3, md: 6 }, textAlign: 'center', maxWidth: 500, borderRadius: 3, boxShadow: '0 10px 40px rgba(0,0,0,0.1)' }}>
          <AvatarMui sx={{ bgcolor: '#fee2e2', color: '#ef4444', width: 80, height: 80, mb: 3, mx: 'auto' }}>
            <WarningIcon sx={{ fontSize: 50 }} />
          </AvatarMui>
          <Typography variant="h4" fontWeight="bold" color="#1a202c" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, fontSize: '1.1rem' }}>
            You do not have permission to view Floor Management. Please contact your administrator for access.
          </Typography>
          <Button
            variant="contained"
            onClick={() => history.push('/app/dashboard')}
            sx={{
              backgroundColor: '#536DFE',
              '&:hover': { backgroundColor: '#3d5afe' },
              px: 4, py: 1.2, borderRadius: 2, fontWeight: 600, textTransform: 'none'
            }}
          >
            Back to Dashboard
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, backgroundColor: "#f5f5f5", minHeight: "100vh" }}>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1, flexDirection: "column" }}
        open={globalLoading}
      >
        <CircularProgress color="inherit" />
        <Typography variant="body2" sx={{ mt: 1.5 }}>
          Please wait...
        </Typography>
      </Backdrop>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ProjectIcon sx={{ mr: 1, color: 'primary.main', fontSize: 28 }} />
            <Typography variant="h4" fontWeight="bold" color="primary">Floor Management</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          {canWrite && (
            <>
              <Button variant="contained" startIcon={<FileUploadIcon />} onClick={handleImportClick}>Import</Button>
              <Button variant="outlined" startIcon={<FileDownloadIcon />} onClick={handleOpenExportMenu}>Export</Button>
              <Menu
                anchorEl={exportMenuAnchorEl}
                open={Boolean(exportMenuAnchorEl)}
                onClose={handleCloseExportMenu}
              >
                <MenuItem onClick={handleExportExcel}>Export as Excel</MenuItem>
                <MenuItem onClick={handleExportCSV}>Export as CSV</MenuItem>
                <MenuItem onClick={handleExportPDF}>Export as PDF</MenuItem>
              </Menu>
            </>
          )}
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleSearch} disabled={!hasSearched}>Refresh</Button>
          {canWrite && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => history.push('/app/project/apartment/floor/add', { building: getSelectedBuilding() })} disabled={!canAddFloor}>Add Floor</Button>
          )}
        </Box>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3} md={2}>
            <FormControl fullWidth>
              <InputLabel shrink>Property Type</InputLabel>
              <Select
                value={draftType}
                onChange={(e) => {
                  const value = e.target.value;
                  setDraftType(value);
                  setDraftProject("all");
                  setDraftBuilding("all");
                  setDraftFloor("all");
                }}
                label="Property Type"
                displayEmpty
              >
                <MenuItem value="all">Select Type</MenuItem>
                <MenuItem value="Apartment">Apartment</MenuItem>
                <MenuItem value="Commercial">Commercial</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3} md={2}>
            <FormControl fullWidth>
              <InputLabel shrink>Project</InputLabel>
              <Select
                value={draftProject}
                onChange={(e) => {
                  setDraftProject(e.target.value);
                  setDraftBuilding("all");
                  setDraftFloor("all");
                }}
                label="Project"
              >
                {projectOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3} md={2}>
            <FormControl fullWidth disabled={draftProject === "all"}>
              <InputLabel shrink>Building</InputLabel>
              <Select
                value={draftBuilding}
                onChange={(e) => {
                  setDraftBuilding(e.target.value);
                  setDraftFloor("all");
                }}
                label="Building"
              >
                <MenuItem value="all">Select Building</MenuItem>
                {apiBuildings.map((building) => (
                  <MenuItem key={building.id} value={building.id}>{building.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3} md={2}>
            <FormControl fullWidth disabled={!hasSearched || selectedFloors.length === 0}>
              <InputLabel shrink>Floor</InputLabel>
              <Select
                value={draftFloor}
                onChange={(e) => setDraftFloor(e.target.value)}
                label="Floor"
              >
                <MenuItem value="all">All Floors</MenuItem>
                {selectedFloors.map((f) => (
                  <MenuItem key={f.id} value={f.id}>{f.name || `Floor ${f.floorNo}`}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3} md={2}>
            <FormControl fullWidth>
              <InputLabel shrink>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(0);
                }}
                label="Status"
              >
                {statusOptions.map((opt) => (
                  <MenuItem key={opt} value={opt}>{opt === "all" ? "All Status" : opt}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm="auto">
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="contained" startIcon={<SearchIcon />} onClick={handleSearch}>Search</Button>
              <Button variant="outlined" onClick={handleReset}>Reset</Button>
            </Box>
          </Grid>
          {searchError && (
            <Grid item xs={12}>
              <Typography variant="body2" color="error">{searchError}</Typography>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* {selectedBuilding && hasSearched && (
        <Card sx={{ mb: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.08)", borderRadius: 2 }}>
          <CardContent sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Building
              </Typography>
              <Typography variant="body2">{selectedBuilding.name}</Typography>
              <Chip
                label={selectedBuilding.project}
                color="primary"
                variant="outlined"
                size="small"
                sx={{ mt: 1, fontWeight: 600 }}
              />
            </Box>
            {selectedFloorItem && (
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Selected Floor
                </Typography>
                <Typography variant="body2">{selectedFloorItem.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {selectedFloorItem.totalFlats} flats · {selectedFloorItem.status}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}  */}

      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            {hasSearched && draftProject !== "all" && (
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                  {activeSelection.building === "all" && <TableCell sx={{ fontWeight: 700 }}>Building</TableCell>}
                  <TableCell sx={{ fontWeight: 700 }}>Floor</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Project</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Total Flats</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Remark</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
            )}
            <TableBody>
              {paginatedFloors.map((floor) => (
                <TableRow hover key={floor.id}>
                  {activeSelection.building === "all" && <TableCell><Typography variant="body2">{floor.buildingName}</Typography></TableCell>}
                  <TableCell><Typography fontWeight="600">{floor.name}</Typography></TableCell>
                  <TableCell><Typography variant="body2" color="text.secondary">{floor.projectName || "—"}</Typography></TableCell>
                  <TableCell align="center">{floor.totalFlats}</TableCell>
                  <TableCell><Chip label={floor.status} size="small" sx={statusChipStyle(floor.status)} /></TableCell>
                  <TableCell><Typography variant="body2" color="text.secondary">{floor.remark || "—"}</Typography></TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                      <IconButton size="small" color="info" onClick={() => handleViewFlats(floor)}><VisibilityIcon /></IconButton>
                      {canWrite ? (
                        <>
                          <IconButton size="small" color="primary" onClick={() => history.push(`/app/project/apartment/floor/edit/${floor.id}`, { floor, building: getSelectedBuilding() })}><EditIcon /></IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDeleteFloor(floor)}><DeleteIcon /></IconButton>
                        </>
                      ) : (
                        <></>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {paginatedFloors.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <Typography variant="body2" color="text.secondary">
                      {hasSearched ? "No floors found for the selected building." : "Select type, project, and building, then click Search to view floors."}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {hasSearched && draftProject !== "all" && (
          <TablePagination
            // rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={selectedFloors.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          />
        )}
      </Paper>
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'primary.main', color: 'white', mb: 2 }}>
          <ProjectIcon />
          <Typography variant="h6">Add New Floor</Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="primary">Configuration</Typography>
          <Divider sx={{ mb: 3 }} />
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel shrink>Property Type</InputLabel>
                <Select
                  value={addDialogType}
                  onChange={(e) => {
                    setAddDialogType(e.target.value);
                    setAddDialogProject("");
                    setNewFloor((prev) => ({ ...prev, buildingId: "" }));
                  }}
                  label="Property Type"
                >
                  {propertyTypeOptions.map((type) => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth disabled={!addDialogType}>
                <InputLabel shrink>Project</InputLabel>
                <Select
                  value={addDialogProject}
                  onChange={(e) => {
                    setAddDialogProject(e.target.value);
                    setNewFloor((prev) => ({ ...prev, buildingId: "" }));
                  }}
                  label="Project"
                >
                  {projects.map((project) => (
                    <MenuItem key={project._id} value={project._id}>{project.projectName}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth disabled={!addDialogProject}>
                <InputLabel shrink>Building</InputLabel>
                <Select
                  value={newFloor.buildingId}
                  onChange={(e) => setNewFloor((prev) => ({ ...prev, buildingId: e.target.value }))}
                  label="Building"
                >
                  {apiBuildings.map((building) => (
                    <MenuItem key={building.id} value={building.id}>{building.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Floor Number"
                type="number"
                value={newFloor.floorNo}
                onChange={handleFloorNoChange(setNewFloor)}
                fullWidth
                inputProps={{ min: 0, max: 100 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Total Flats"
                type="number"
                value={newFloor.totalFlats}
                onChange={handleTotalFlatsChange(setNewFloor)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel shrink>Status</InputLabel>
                <Select
                  value={newFloor.status}
                  onChange={(e) => setNewFloor((prev) => ({ ...prev, status: e.target.value }))}
                  label="Status"
                >
                  <MenuItem value="Available">Available</MenuItem>
                  <MenuItem value="Under Construction">Under Construction</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Remark"
                value={newFloor.remark}
                onChange={(e) => setNewFloor((prev) => ({ ...prev, remark: e.target.value }))}
                fullWidth
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
          {dialogError && addDialogOpen && (
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>{dialogError}</Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setAddDialogOpen(false)} color="inherit">Cancel</Button>
          <Button variant="contained" onClick={handleSaveNewFloor}>Save Floor</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'primary.main', color: 'white', mb: 2 }}>
          <EditIcon />
          <Typography variant="h6">Edit Floor</Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom color="primary">Configuration</Typography>
          <Divider sx={{ mb: 3 }} />
          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth disabled>
                <InputLabel shrink>Property Type</InputLabel>
                <Select value={editDialogType} label="Property Type">
                  {propertyTypeOptions.map((type) => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth disabled>
                <InputLabel shrink>Project</InputLabel>
                <Select value={editDialogProject} label="Project">
                  {projects.map((project) => (
                    <MenuItem key={project._id} value={project._id}>{project.projectName}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth disabled>
                <InputLabel shrink>Building</InputLabel>
                <Select value={editFloorState.buildingId} label="Building">
                  {apiBuildings.map((building) => (
                    <MenuItem key={building.id} value={building.id}>{building.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Floor Name" value={editFloorState.name} fullWidth disabled />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Total Flats"
                type="number"
                value={editFloorState.totalFlats}
                onChange={handleTotalFlatsChange(setEditFloorState)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel shrink>Status</InputLabel>
                <Select
                  value={editFloorState.status}
                  onChange={(e) => setEditFloorState((prev) => ({ ...prev, status: e.target.value }))}
                  label="Status"
                >
                  <MenuItem value="Available">Available</MenuItem>
                  <MenuItem value="Under Construction">Under Construction</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Remark"
                value={editFloorState.remark}
                onChange={(e) => setEditFloorState((prev) => ({ ...prev, remark: e.target.value }))}
                fullWidth
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
          {dialogError && editDialogOpen && (
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>{dialogError}</Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setEditDialogOpen(false)} color="inherit">Cancel</Button>
          <Button variant="contained" onClick={handleUpdateFloor} disabled={editSaving}>
            {editSaving ? "Saving..." : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={importDialogOpen} onClose={handleCloseImportDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Import Floors</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <DialogContentText>
              Upload an Excel file (.xlsx or .xls) with columns such as <code>buildingId</code>,{" "}
              <code>name</code>, <code>totalFlats</code>, <code>status</code>, and <code>remark</code>.
              Rows missing a building identifier or floor name will be skipped.
            </DialogContentText>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <Button
                variant="text"
                startIcon={<FileDownloadIcon />}
                sx={{ textTransform: "none" }}
                onClick={handleDownloadSample}
              >
                Download Sample
              </Button>
              <Button
                variant="text"
                startIcon={<FileDownloadIcon />}
                sx={{ textTransform: "none" }}
                onClick={handleDownloadTemplate}
              >
                Download Template
              </Button>
            </Stack>
            {importError && (
              <Alert severity="error" onClose={() => setImportError("")}>
                {importError}
              </Alert>
            )}
            {importSuccess && (
              <Alert severity="success" onClose={() => setImportSuccess("")}>
                {importSuccess}
              </Alert>
            )}
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Button
                variant="outlined"
                sx={{ textTransform: "none" }}
                onClick={() => fileInputRef.current?.click()}
              >
                Choose File
              </Button>
              {importFileName && (
                <Typography variant="caption" color="text.secondary">
                  {importFileName}
                </Typography>
              )}
            </Stack>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              hidden
              onChange={handleChooseImportFile}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseImportDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handlePerformImport}
            disabled={!selectedImportFile}
          >
            Import
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FloorList;
